import { useContext, useMemo } from 'react'
import { useLocation } from 'react-router-dom'

import {
  routes,
  getPathAncestors,
  RouteNode,
  findMatchRoute,
} from 'packages/route'
import { LayoutContext } from 'packages/web-layout/context'

export type RouteCrumb = Partial<RouteNode> & {
  realPath?: string
  search?: string
}

export function buildBreadcrumbs(
  pathname: string,
  search: string,
  prevBreadcrumbs?: RouteCrumb[],
) {
  const pathAncestors = getPathAncestors(pathname)
  return pathAncestors.reduce<RouteCrumb[]>((res, cur, idx) => {
    const isLastCrumb = idx === pathAncestors.length - 1
    const prevSameCrumb = prevBreadcrumbs?.find(crumb => crumb.realPath === cur)
    const route = findMatchRoute(cur, routes)
    return route
      ? [
          ...res,
          {
            ...route,
            realPath: cur,
            search: isLastCrumb ? search : prevSameCrumb?.search,
          },
        ]
      : res
  }, [])
}

let breadcrumbs: RouteCrumb[] | undefined

export const useBreadcrumbs = () => {
  const layoutContext = useContext(LayoutContext)
  const { formatBreadcrumbs } = layoutContext

  const location = useLocation()

  breadcrumbs = useMemo(() => {
    const { pathname, search } = location
    return buildBreadcrumbs(pathname, search, breadcrumbs)
  }, [location])

  return formatBreadcrumbs ? formatBreadcrumbs(breadcrumbs) : breadcrumbs
}
